<template>
  <v-card class="sheet-columns-config" elevation="2">
    <!-- Section 1: Sheet & Columns -->
    <v-card-title class="section-title">
      <h3>Sheet & Columns</h3>
    </v-card-title>
    
    <v-card-text>
      <!-- Success notification with ARIA live region -->
      <v-alert
        v-model="showSuccessAlert"
        type="success"
        dismissible
        transition="fade-transition"
        class="mb-4"
        role="alert"
        aria-live="polite"
      >
        Configuration saved successfully!
      </v-alert>

      <!-- Error notification with ARIA live region -->
      <v-alert
        v-model="showErrorAlert"
        type="error"
        dismissible
        transition="fade-transition"
        class="mb-4"
        role="alert"
        aria-live="polite"
      >
        Error saving configuration. Please try again.
      </v-alert>

      <v-form ref="sheetForm" v-model="isFormValid">
        <v-row>
          <!-- Title Input -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.title"
              label="Title"
              outlined
              dense
              required
              :rules="titleRules"
              :error-messages="titleErrors"
              hint="Enter a descriptive title for this configuration"
              persistent-hint
            />
          </v-col>

          <!-- Source Select -->
          <v-col cols="12" md="6">
            <v-select
              v-model="formData.source"
              :items="workSources"
              label="Source"
              outlined
              dense
              required
              :rules="sourceRules"
              :error-messages="sourceErrors"
              hint="Select the work source"
              persistent-hint
              item-text="value"
              item-value="id"
            />
          </v-col>

          <!-- Excel File Upload -->
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <v-card-title class="pa-0 mb-3">
                <v-icon left color="primary">mdi-file-excel</v-icon>
                Excel File Upload (Optional)
              </v-card-title>
              <v-card-subtitle class="pa-0 mb-3">
                Upload an Excel file to populate column dropdown options and automatically detect column mappings
              </v-card-subtitle>
              
              <v-file-input
                v-model="uploadedFile"
                accept=".xlsx,.xls"
                label="Choose Excel File"
                prepend-icon="mdi-file-excel"
                outlined
                dense
                :loading="isProcessingFile"
                @change="handleFileUpload"
                clearable
              />
              
              <!-- Column Detection Results -->
              <v-alert
                v-if="showColumnPreview && detectedColumns.firstName !== null"
                type="success"
                class="mt-3"
                dismissible
              >
                <div class="text-subtitle-2 mb-2">Detected Columns:</div>
                <div class="detected-columns">
                  <v-chip
                    v-if="detectedColumns.firstName !== null"
                    color="green"
                    small
                    class="mr-2 mb-1"
                  >
                    First Name: {{ excelHeaders[detectedColumns.firstName]?.text || 'Unknown' }}
                  </v-chip>
                  <v-chip
                    v-if="detectedColumns.lastName !== null"
                    color="blue"
                    small
                    class="mr-2 mb-1"
                  >
                    Last Name: {{ excelHeaders[detectedColumns.lastName]?.text || 'Unknown' }}
                  </v-chip>
                  <v-chip
                    v-if="detectedColumns.mobile !== null"
                    color="orange"
                    small
                    class="mr-2 mb-1"
                  >
                    Mobile: {{ excelHeaders[detectedColumns.mobile]?.text || 'Unknown' }}
                  </v-chip>
                </div>
                <v-btn
                  color="primary"
                  small
                  @click="applyDetectedColumns"
                  class="mt-2"
                >
                  <v-icon left small>mdi-check</v-icon>
                  Apply Detected Columns
                </v-btn>
              </v-alert>

              <!-- Excel Preview Table -->
              <v-data-table
                v-if="showColumnPreview && excelPreview.length > 0 && 0 "
                :headers="excelPreviewHeaders"
                :items="excelPreview.slice(0, 5)"
                :items-per-page="5"
                class="mt-3"
                dense
                hide-default-footer
              >
                <template #[`item.index`]="{ index }">
                  <v-chip
                    :color="getColumnColor(index)"
                    small
                    outlined
                  >
                    {{ index + 1 }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <!-- Sheet ID Input -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.sheetId"
              label="Sheet ID"
              outlined
              dense
              required
              :rules="sheetIdRules"
              :error-messages="sheetIdErrors"
              hint="Enter the Google Sheets ID"
              persistent-hint
            />
          </v-col>

          <!-- First Name Column Select -->
          <v-col cols="12" md="6">
            <v-select
              v-model="formData.firstNameColumn"
              :items="excelHeaders"
              label="First Name Column"
              outlined
              dense
              required
              :rules="columnSelectRules"
              :error-messages="firstNameColumnErrors"
              hint="Select the column containing first names"
              persistent-hint
              :disabled="excelHeaders.length === 0"
              placeholder="Upload Excel file to see column options"
            />
          </v-col>

          <!-- Last Name Column Select -->
          <v-col cols="12" md="6">
            <v-select
              v-model="formData.lastNameColumn"
              :items="excelHeaders"
              label="Last Name Column"
              outlined
              dense
              required
              :rules="columnSelectRules"
              :error-messages="lastNameColumnErrors"
              hint="Select the column containing last names"
              persistent-hint
              :disabled="excelHeaders.length === 0"
              placeholder="Upload Excel file to see column options"
            />
          </v-col>

          <!-- Mobile Column Select -->
          <v-col cols="12" md="6">
            <v-select
              v-model="formData.mobileColumn"
              :items="excelHeaders"
              label="Mobile Column"
              outlined
              dense
              required
              :rules="columnSelectRules"
              :error-messages="mobileColumnErrors"
              hint="Select the column containing mobile numbers"
              persistent-hint
              :disabled="excelHeaders.length === 0"
              placeholder="Upload Excel file to see column options"
            />
          </v-col>
        </v-row>

        <!-- Save Button -->
        <v-row>
          <v-col cols="12">
            <v-btn
              color="primary"
              :disabled="!isFormValid"
              @click="saveConfiguration"
              class="save-btn"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <!-- Section 2: Processing Status -->
    <v-divider class="my-4"></v-divider>
    
    <v-card-title class="section-title">
      <h3>Processing Summary</h3>
    </v-card-title>
    
    <v-card-text>
      <v-row>
        <!-- Duplicated Users Stat -->
        <v-col cols="12" md="4">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <div class="stat-number">{{ processingStats.duplicatedUsers }}</div>
              <div class="stat-label">Duplicated Users</div>
              <div class="stat-description">
                <small>Detected multiple entries with the same mobile</small>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- New Users Stat -->
        <v-col cols="12" md="4">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <div class="stat-number">{{ processingStats.newUsers }}</div>
              <div class="stat-label">New Users</div>
              <div class="stat-description">
                <small>Entries not seen before</small>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Overall Status -->
        <v-col cols="12" md="4">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <v-chip
                :color="overallStatusColor"
                :class="overallStatusClass"
                class="status-chip"
              >
                {{ overallStatusText }}
              </v-chip>
              <div class="stat-description mt-2">
                <small>Processing Status</small>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Refresh Button -->
      <v-row class="mt-4">
        <v-col cols="12" class="text-center">
          <v-btn
            color="secondary"
            :loading="isRefreshing"
            :disabled="isRefreshing"
            @click="refreshStats"
            class="refresh-btn"
          >
            <v-icon left v-if="!isRefreshing">mdi-refresh</v-icon>
            <span v-if="isRefreshing">Refreshing...</span>
            <span v-else>Refresh</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Section 3: History Table -->
    <v-divider class="my-4"></v-divider>
    
    <v-card-title class="section-title">
      <h3>History - Old Sheets Results</h3>
    </v-card-title>
    
    <v-card-text>
      <!-- History Table -->
      <v-data-table
        :headers="historyHeaders"
        :items="historyData"
        :loading="isLoadingHistory"
        :items-per-page="10"
        class="elevation-1"
        :search="historySearch"
      >
        <!-- Title Column -->
        <template #[`item.title`]="{ item }">
          <div class="history-title">
            <v-icon left color="primary">mdi-file-document</v-icon>
            {{ item.title }}
          </div>
        </template>

        <!-- Source Column -->
        <template #[`item.source`]="{ item }">
          <v-chip
            :color="getSourceColor(item.source)"
            small
            outlined
          >
            {{ getSourceName(item.source) }}
          </v-chip>
        </template>

        <!-- Sheet ID Column -->
        <template #[`item.sheetId`]="{ item }">
          <div class="sheet-id-cell">
            <code>{{ truncateSheetId(item.sheetId) }}</code>
            <v-btn
              icon
              small
              @click="copyToClipboard(item.sheetId)"
              class="ml-2"
            >
              <v-icon small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Status Column -->
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            :class="getStatusClass(item.status)"
            small
          >
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Results Column -->
        <template #[`item.results`]="{ item }">
          <div class="results-cell">
            <div class="result-item">
              <v-icon small color="orange" class="mr-1">mdi-account-multiple</v-icon>
              <span class="result-label">Duplicated:</span>
              <span class="result-value">{{ item.results.duplicatedUsers }}</span>
            </div>
            <div class="result-item">
              <v-icon small color="green" class="mr-1">mdi-account-plus</v-icon>
              <span class="result-label">New:</span>
              <span class="result-value">{{ item.results.newUsers }}</span>
            </div>
          </div>
        </template>

        <!-- Processed Date Column -->
        <template #[`item.processedDate`]="{ item }">
          <div class="date-cell">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            {{ formatDate(item.processedDate) }}
          </div>
        </template>

        <!-- Actions Column -->
        <template #[`item.actions`]="{ item }">
          <div class="actions-cell">
            <v-btn
              icon
              small
              @click="viewDetails(item)"
              color="primary"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="downloadResults(item)"
              color="success"
            >
              <v-icon small>mdi-download</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="deleteHistoryItem(item)"
              color="error"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- No Data Template -->
        <template v-slot:no-data>
          <div class="text-center pa-4">
            <v-icon size="64" color="grey lighten-1">mdi-history</v-icon>
            <div class="text-h6 grey--text mt-2">No History Available</div>
            <div class="text-body-2 grey--text">Processed sheets will appear here</div>
          </div>
        </template>
      </v-data-table>

      <!-- Search and Actions -->
      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="historySearch"
            label="Search history..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
          />
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn
            color="primary"
            @click="loadHistory"
            :loading="isLoadingHistory"
            class="mr-2"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh History
          </v-btn>
          <v-btn
            color="secondary"
            @click="clearHistory"
            :disabled="historyData.length === 0"
          >
            <v-icon left>mdi-delete-sweep</v-icon>
            Clear All
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'SheetColumnsConfig',
  data() {
    return {
      // Form data
      formData: {
        title: '',
        source: null,
        sheetId: '',
        firstNameColumn: null,
        lastNameColumn: null,
        mobileColumn: null
      },
      
      // Excel headers for dropdown options
      excelHeaders: [],
      
      // Form validation
      isFormValid: false,
      showSuccessAlert: false,
      showErrorAlert: false,
      
      // Processing stats
      processingStats: {
        duplicatedUsers: 0,
        newUsers: 0
      },
      
      // Refresh state
      isRefreshing: false,
      
      // History table data
      historyData: [],
      isLoadingHistory: false,
      historySearch: '',
      
      // Excel file upload
      uploadedFile: null,
      isProcessingFile: false,
      detectedColumns: {
        firstName: null,
        lastName: null,
        mobile: null
      },
      excelPreview: [],
      showColumnPreview: false,
      
      // History table headers
      historyHeaders: [
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Source', value: 'source', sortable: true },
        { text: 'Sheet ID', value: 'sheetId', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Results', value: 'results', sortable: false },
        { text: 'Processed Date', value: 'processedDate', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ],
      
      // Excel preview headers (will be dynamically generated)
      excelPreviewHeaders: [],
      
      // Validation rules
      titleRules: [
        v => !!v || 'Title is required',
        v => (v && v.length > 0) || 'Title cannot be empty'
      ],
      
      sourceRules: [
        v => v !== null && v !== undefined || 'Source is required'
      ],
      
      sheetIdRules: [
        v => !!v || 'Sheet ID is required',
        v => (v && v.length > 0) || 'Sheet ID cannot be empty'
      ],
      
      columnSelectRules: [
        v => !!v || 'Column selection is required'
      ]
    }
  },
  
  computed: {
    // Work sources from store
    workSources() {
      return this.$store.state.work_sources || [];
    },
    
    // Error messages for each field
    titleErrors() {
      return this.getFieldErrors('title');
    },
    
    sourceErrors() {
      return this.getFieldErrors('source');
    },
    
    sheetIdErrors() {
      return this.getFieldErrors('sheetId');
    },
    
    firstNameColumnErrors() {
      return this.getFieldErrors('firstNameColumn');
    },
    
    lastNameColumnErrors() {
      return this.getFieldErrors('lastNameColumn');
    },
    
    mobileColumnErrors() {
      return this.getFieldErrors('mobileColumn');
    },
    
    // Overall status computed properties
    overallStatusText() {
      const total = this.processingStats.duplicatedUsers + this.processingStats.newUsers;
      return total === 0 ? 'All Sheets Processed' : 'In Processing';
    },
    
    overallStatusColor() {
      const total = this.processingStats.duplicatedUsers + this.processingStats.newUsers;
      return total === 0 ? 'green' : 'amber';
    },
    
    overallStatusClass() {
      const total = this.processingStats.duplicatedUsers + this.processingStats.newUsers;
      return total === 0 ? 'success-chip' : 'processing-chip';
    }
  },
  
  mounted() {
    this.loadConfiguration();
    this.refreshStats();
    this.loadHistory();
  },
  
  methods: {
    // Load configuration from localStorage
    loadConfiguration() {
      try {
        const saved = localStorage.getItem('sheetColumnsConfig');
        if (saved) {
          const config = JSON.parse(saved);
          this.formData = { ...this.formData, ...config };
        }
      } catch (error) {
        console.error('Error loading configuration:', error);
      }
    },
    
    // Convert column name to index
    getColumnIndex(columnName) {
      if (!columnName || !this.excelHeaders.length) {
        return null;
      }
      
      const header = this.excelHeaders.find(h => h.value === columnName);
      return header ? header.index + 1 : null; // Convert 0-based to 1-based index
    },
    
    // Convert column index to name (for display purposes)
    getColumnName(columnIndex) {
      if (!columnIndex || !this.excelHeaders.length) {
        return `Column ${columnIndex}`;
      }
      
      const header = this.excelHeaders.find(h => h.index === (columnIndex - 1)); // Convert 1-based to 0-based
      return header ? header.text : `Column ${columnIndex}`;
    },
    
    // Save configuration to Laravel API
    async saveConfiguration() {
      if (!this.isFormValid) {
        return;
      }
      
      try {
        // Import axios instance
        const { axiosInstance } = await import('@/axios/config');
        
        // Convert column names to indices
        const firstNameIndex = this.getColumnIndex(this.formData.firstNameColumn);
        const lastNameIndex = this.getColumnIndex(this.formData.lastNameColumn);
        const mobileIndex = this.getColumnIndex(this.formData.mobileColumn);
        
        // Validate that all column indices are valid
        if (firstNameIndex === null || lastNameIndex === null || mobileIndex === null) {
          throw new Error('Invalid column selection. Please ensure all columns are properly selected.');
        }
        
        // Prepare data for Laravel API
        const apiData = {
          title: this.formData.title,
          source_id: this.formData.source,
          source: this.formData.source,
          sheet_id: this.formData.sheetId,
          firstNameColumn: firstNameIndex - 1,
          lastNameColumn: lastNameIndex - 1,
          mobileColumn: mobileIndex - 1
        };
        
        // POST to Laravel API
        const response = await axiosInstance.post('/v1/uploaded-sheets', apiData);
        
        // Save to localStorage as backup
        localStorage.setItem('sheetColumnsConfig', JSON.stringify(this.formData));
        
        // Show success notification
        this.showSuccessAlert = true;
        
        // Auto-hide success alert after 2 seconds
        setTimeout(() => {
          this.showSuccessAlert = false;
        }, 2000);
        
        // Emit event for parent components with the payload data
        this.$emit('configuration-saved', response.data.payload);
        
        // Refresh history after successful save
        this.loadHistory();
        
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.$emit('configuration-error', error);
        
        // Show error notification
        this.showErrorAlert = true;
        setTimeout(() => {
          this.showErrorAlert = false;
        }, 3000);
      }
    },
    
    // Get field-specific errors
    getFieldErrors(fieldName) {
      const rules = this.getRulesForField(fieldName);
      const value = this.formData[fieldName];
      
      for (const rule of rules) {
        const error = rule(value);
        if (error !== true) {
          return [error];
        }
      }
      return [];
    },
    
    // Get validation rules for specific field
    getRulesForField(fieldName) {
      switch (fieldName) {
        case 'title':
          return this.titleRules;
        case 'source':
          return this.sourceRules;
        case 'sheetId':
          return this.sheetIdRules;
        case 'firstNameColumn':
        case 'lastNameColumn':
        case 'mobileColumn':
          return this.columnSelectRules;
        default:
          return [];
      }
    },
    
    // Refresh processing stats
    async refreshStats() {
      this.isRefreshing = true;
      
      try {
        // Simulate API call with setTimeout
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Generate mock data
        this.processingStats = {
          duplicatedUsers: Math.floor(Math.random() * 50),
          newUsers: Math.floor(Math.random() * 100)
        };
        
        this.$emit('stats-refreshed', this.processingStats);
        
      } catch (error) {
        console.error('Error refreshing stats:', error);
        this.$emit('stats-error', error);
      } finally {
        this.isRefreshing = false;
      }
    },
    
    // History table methods
    async loadHistory() {
      this.isLoadingHistory = true;
      
      try {
        // Import axios instance
        const { axiosInstance } = await import('@/axios/config');
        
        // GET from Laravel API
        const response = await axiosInstance.get('/v1/uploaded-sheets');
        
        // Transform Laravel API response to match component data structure
        this.historyData = response.data.payload.map(item => ({
          id: item.id,
          title: item.title,
          source: item.source,
          sheetId: item.sheet_id,
          status: item.status || 'Completed',
          results: {
            duplicatedUsers: item.duplicated || 0,
            newUsers: item.new || 0
          },
          processedDate: new Date(item.created_at || item.updated_at),
          // Store original column indices for reference
          columnIndices: {
            firstName: item.first_name_column,
            lastName: item.last_name_column,
            mobile: item.mobile_column
          }
        }));
        
        this.$emit('history-loaded', this.historyData);
        
      } catch (error) {
        console.error('Error loading history:', error);
        this.$emit('history-error', error);
        
        // Fallback to empty array on error
        this.historyData = [];
      } finally {
        this.isLoadingHistory = false;
      }
    },
    
    getSourceName(sourceId) {
      const source = this.workSources.find(s => s.id == sourceId);
      return source ? source.value : 'Unknown Source';
    },
    
    getSourceColor(sourceId) {
      const colors = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
      return colors[sourceId % colors.length] || 'grey';
    },
    
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'error';
        case 'processing':
          return 'warning';
        default:
          return 'grey';
      }
    },
    
    getStatusClass(status) {
      switch (status.toLowerCase()) {
        case 'completed':
          return 'success-chip';
        case 'failed':
          return 'error-chip';
        case 'processing':
          return 'processing-chip';
        default:
          return 'grey-chip';
      }
    },
    
    truncateSheetId(sheetId) {
      if (!sheetId) return '';
      return sheetId.length > 20 ? `${sheetId.substring(0, 20)}...` : sheetId;
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$emit('clipboard-copied', text);
        // You could show a toast notification here
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        this.$emit('clipboard-error', error);
      }
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    viewDetails(item) {
      this.$emit('view-history-details', item);
      // You could open a dialog or navigate to a details page
    },
    
    async downloadResults(item) {
      try {
        // Import axios instance
        const { axiosInstance } = await import('@/axios/config');
        
        // GET download endpoint from Laravel API
        const response = await axiosInstance.get(`/v1/uploaded-sheets/${item.id}/download`, {
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${item.title}_results.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$emit('download-history-results', item);
        
      } catch (error) {
        console.error('Error downloading results:', error);
        this.$emit('download-error', error);
      }
    },
    
    async deleteHistoryItem(item) {
      if (confirm(`Are you sure you want to delete "${item.title}" from history?`)) {
        try {
          // Import axios instance
          const { axiosInstance } = await import('@/axios/config');
          
          // DELETE from Laravel API
          await axiosInstance.delete(`/v1/uploaded-sheets/${item.id}`);
          
          // Remove from local data
          const index = this.historyData.findIndex(h => h.id === item.id);
          if (index > -1) {
            this.historyData.splice(index, 1);
          }
          
          this.$emit('history-item-deleted', item);
          
        } catch (error) {
          console.error('Error deleting history item:', error);
          this.$emit('delete-error', error);
        }
      }
    },
    
    async clearHistory() {
      if (confirm('Are you sure you want to clear all history? This action cannot be undone.')) {
        try {
          // Import axios instance
          const { axiosInstance } = await import('@/axios/config');
          
          // DELETE all from Laravel API
          await axiosInstance.delete('/v1/uploaded-sheets/clear-all');
          
          // Clear local data
          this.historyData = [];
          this.$emit('history-cleared');
          
        } catch (error) {
          console.error('Error clearing history:', error);
          this.$emit('clear-error', error);
        }
      }
    },
    
    // Excel file handling methods
    async handleFileUpload(file) {
      if (!file) {
        this.resetExcelData();
        return;
      }
      
      this.isProcessingFile = true;
      
      try {
        // Import XLSX library dynamically
        const XLSX = await import('xlsx');
        
        // Read the file
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Get the first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length === 0) {
              throw new Error('Excel file is empty');
            }
            
            // Process the data
            this.processExcelData(jsonData);
            
          } catch (error) {
            console.error('Error processing Excel file:', error);
            this.$emit('excel-error', error);
          } finally {
            this.isProcessingFile = false;
          }
        };
        
        reader.readAsArrayBuffer(file);
        
      } catch (error) {
        console.error('Error reading Excel file:', error);
        this.$emit('excel-error', error);
        this.isProcessingFile = false;
      }
    },
    
    processExcelData(data) {
      if (data.length < 2) {
        throw new Error('Excel file must have at least 2 rows (header + data)');
      }
      
      // Get headers from first row
      const headers = data[0];
      const dataRows = data.slice(1, 6); // Take first 5 data rows for preview
      
      // Extract headers for dropdown options
      this.excelHeaders = headers.map((header, index) => ({
        text: header || `Column ${index + 1}`,
        value: header || `Column ${index + 1}`,
        index: index
      }));
      
      // Detect column indices
      this.detectColumns(headers);
      
      // Create preview data
      this.createPreviewData(headers, dataRows);
      
      // Show preview
      this.showColumnPreview = true;
      
      this.$emit('excel-processed', {
        headers,
        detectedColumns: this.detectedColumns,
        previewData: this.excelPreview,
        excelHeaders: this.excelHeaders
      });
    },
    
    detectColumns(headers) {
      // Reset detected columns
      this.detectedColumns = {
        firstName: null,
        lastName: null,
        mobile: null
      };
      
      // Common patterns for column detection
      const firstNamePatterns = [
        'first_name', 'firstname', 'first name', 'fname', 'given_name', 'givenname',
        'الاسم الأول', 'الاسم الاول', 'الاسم_الأول', 'الاسم_الاول'
      ];
      
      const lastNamePatterns = [
        'last_name', 'lastname', 'last name', 'lname', 'surname', 'family_name', 'familyname',
        'الاسم الأخير', 'الاسم الاخير', 'الاسم_الأخير', 'الاسم_الاخير', 'اسم العائلة'
      ];
      
      const mobilePatterns = [
        'mobile', 'phone', 'telephone', 'cell', 'contact', 'phone_number', 'mobile_number',
        'الجوال', 'الهاتف', 'رقم الجوال', 'رقم الهاتف', 'التليفون'
      ];
      
      // Detect columns
      headers.forEach((header, index) => {
        if (!header) return;
        
        const headerLower = header.toString().toLowerCase().trim();
        
        // Check for first name
        if (this.detectedColumns.firstName === null && 
            firstNamePatterns.some(pattern => headerLower.includes(pattern))) {
          this.detectedColumns.firstName = index;
        }
        
        // Check for last name
        if (this.detectedColumns.lastName === null && 
            lastNamePatterns.some(pattern => headerLower.includes(pattern))) {
          this.detectedColumns.lastName = index;
        }
        
        // Check for mobile
        if (this.detectedColumns.mobile === null && 
            mobilePatterns.some(pattern => headerLower.includes(pattern))) {
          this.detectedColumns.mobile = index;
        }
      });
    },
    
    createPreviewData(headers, dataRows) {
      // Create headers for preview table
      this.excelPreviewHeaders = [
        { text: 'Column', value: 'index', sortable: false, width: '80px' },
        ...headers.map((header, index) => ({
          text: header || `Column ${index + 1}`,
          value: `col_${index}`,
          sortable: false
        }))
      ];
      
      // Create preview data
    /*this.excelPreview = dataRows.map((row, rowIndex) => {
        const item = { index: rowIndex + 1 };
        headers.forEach((header, colIndex) => {
          item[`col_${colIndex}`] = row[colIndex] || '';
        });
        return item;
      });*/
    },
    
    applyDetectedColumns() {
      if (this.detectedColumns.firstName !== null) {
        const header = this.excelHeaders[this.detectedColumns.firstName];
        this.formData.firstNameColumn = header ? header.value : null;
      }
      if (this.detectedColumns.lastName !== null) {
        const header = this.excelHeaders[this.detectedColumns.lastName];
        this.formData.lastNameColumn = header ? header.value : null;
      }
      if (this.detectedColumns.mobile !== null) {
        const header = this.excelHeaders[this.detectedColumns.mobile];
        this.formData.mobileColumn = header ? header.value : null;
      }
      
      this.$emit('columns-applied', this.detectedColumns);
    },
    
    getColumnColor(index) {
      if (this.detectedColumns.firstName === index) return 'green';
      if (this.detectedColumns.lastName === index) return 'blue';
      if (this.detectedColumns.mobile === index) return 'orange';
      return 'grey';
    },
    
    resetExcelData() {
      this.uploadedFile = null;
      this.detectedColumns = {
        firstName: null,
        lastName: null,
        mobile: null
      };
      this.excelPreview = [];
      this.showColumnPreview = false;
      this.excelPreviewHeaders = [];
      this.excelHeaders = [];
      
      // Reset form column selections
      this.formData.firstNameColumn = null;
      this.formData.lastNameColumn = null;
      this.formData.mobileColumn = null;
    }
  }
}
</script>

<style scoped>
.sheet-columns-config {
  max-width: 1000px;
  margin: 0 auto;
}

.section-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.section-title h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: #333;
}

.stat-card {
  height: 100%;
  transition: transform 0.2s ease-in-out;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.stat-description {
  color: #666;
  font-size: 0.9rem;
}

.status-chip {
  font-weight: 500;
  font-size: 1rem;
  padding: 8px 16px;
}

.success-chip {
  background-color: #4caf50 !important;
  color: white !important;
}

.processing-chip {
  background-color: #ff9800 !important;
  color: white !important;
}

.save-btn {
  margin-top: 1rem;
}

.refresh-btn {
  margin-top: 1rem;
}

/* Dark mode support */
.dark .section-title {
  background-color: #424242;
  border-bottom-color: #616161;
}

.dark .section-title h3 {
  color: #fff;
}

.dark .stat-card {
  background-color: #424242;
}

.dark .stat-number {
  color: #64b5f6;
}

.dark .stat-label {
  color: #fff;
}

.dark .stat-description {
  color: #b0b0b0;
}

/* History table styles */
.history-title {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.sheet-id-cell {
  display: flex;
  align-items: center;
  font-family: 'Courier New', monospace;
}

.sheet-id-cell code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.results-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-item {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.result-label {
  font-weight: 500;
  margin-right: 4px;
}

.result-value {
  font-weight: bold;
  color: #1976d2;
}

.date-cell {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.actions-cell {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.error-chip {
  background-color: #f44336 !important;
  color: white !important;
}

.grey-chip {
  background-color: #9e9e9e !important;
  color: white !important;
}

/* Dark mode support for history table */
.dark .sheet-id-cell code {
  background-color: #424242;
  color: #fff;
}

.dark .result-value {
  color: #64b5f6;
}

/* Excel upload styles */
.detected-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.excel-preview-table {
  max-height: 300px;
  overflow-y: auto;
}

/* Responsive adjustments for Excel section */
@media (max-width: 768px) {
  .detected-columns {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .detected-columns .v-chip {
    margin-bottom: 4px;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stat-number {
    font-size: 2rem;
  }
  
  .section-title h3 {
    font-size: 1.1rem;
  }
  
  .actions-cell {
    flex-direction: column;
    gap: 2px;
  }
  
  .results-cell {
    font-size: 0.8rem;
  }
}
</style>
